import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reauthenticateWithCredential, EmailAuthProvider, updatePassword } from 'firebase/auth';
import './Auth.css';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const ChangePassword: React.FC = () => {
    const { user } = useAuth();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setSuccess('');
        setError('');
        if (!oldPassword || !newPassword || !confirmPassword) {
            setError('Please fill in all fields');
            return;
        }
        if (newPassword.length < 6) {
            setError('New password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            return;
        }
        if (!user || !user.email) {
            setError('User not found');
            return;
        }
        setSaving(true);
        try {
            const credential = EmailAuthProvider.credential(user.email, oldPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);
            setSuccess('Password changed successfully!');
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (e: any) {
            if (e.code === 'auth/wrong-password') {
                setError('Old password is incorrect');
            } else {
                setError(e.message || 'Failed to change password');
            }
        }
        setSaving(false);
    };

    return (
        <div className="auth-container">
            <div className="auth-box">
                <h2>Change Password</h2>
                <form onSubmit={handleChangePassword}>
                    <div className="password-field">
                        <input
                            type={showOld ? 'text' : 'password'}
                            placeholder="Old Password"
                            value={oldPassword}
                            onChange={e => setOldPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                        />
                        <button
                            type="button"
                            className="show-btn"
                            onClick={() => setShowOld(v => !v)}
                            tabIndex={-1}
                            aria-label={showOld ? 'Hide password' : 'Show password'}
                        >
                            {showOld ? <FiEyeOff /> : <FiEye />}
                        </button>
                    </div>
                    <div className="password-field">
                        <input
                            type={showNew ? 'text' : 'password'}
                            placeholder="New Password (min 6 chars)"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            required
                            minLength={6}
                            autoComplete="new-password"
                        />
                        <button
                            type="button"
                            className="show-btn"
                            onClick={() => setShowNew(v => !v)}
                            tabIndex={-1}
                            aria-label={showNew ? 'Hide password' : 'Show password'}
                        >
                            {showNew ? <FiEyeOff /> : <FiEye />}
                        </button>
                    </div>
                    <div className="password-field">
                        <input
                            type={showConfirm ? 'text' : 'password'}
                            placeholder="Confirm New Password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                            minLength={6}
                            autoComplete="new-password"
                        />
                        <button
                            type="button"
                            className="show-btn"
                            onClick={() => setShowConfirm(v => !v)}
                            tabIndex={-1}
                            aria-label={showConfirm ? 'Hide password' : 'Show password'}
                        >
                            {showConfirm ? <FiEyeOff /> : <FiEye />}
                        </button>
                    </div>
                    <button
                        type="submit"
                        disabled={saving}
                        style={{
                            width: '100%',
                            background: '#27ae60',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            padding: '12px 0',
                            fontWeight: 600,
                            fontSize: 18,
                            cursor: saving ? 'not-allowed' : 'pointer',
                            marginBottom: 10
                        }}
                    >
                        {saving ? 'Saving...' : 'Change Password'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/profile')}
                        style={{
                            width: '100%',
                            background: '#f5f5f5',
                            color: '#222',
                            border: '1px solid #ccc',
                            borderRadius: 6,
                            padding: '12px 0',
                            fontWeight: 600,
                            fontSize: 18,
                            cursor: 'pointer',
                            marginBottom: 6
                        }}
                    >
                        Back
                    </button>
                </form>
                {success && <div style={{ color: 'green', marginTop: 10 }}>{success}</div>}
                {error && <div style={{ color: 'red', marginTop: 10 }}>{error}</div>}
            </div>
        </div>
    );
};

export default ChangePassword; 